import {
  Box,
  CheckBox,
  Flex,
  IconButton,
  Menu,
  Text,
  TextField,
  TrashIcon,
  UserSelect,
} from 'components'
import { useWorkspace } from 'components/providers'
import { isEmpty, startCase } from 'lodash-es'
import { IEvent, Lead, leadProperties, LeadTemperature, leadTemperatures } from 'types'
import LeadTempIcon from '../LeadTempIcon'

interface Props {
  allowEdit: boolean
  leadDefaults?: Partial<Lead>
  onChange: (leadDefaults: IEvent['leadDefaults']) => void
}

export default function EventLeadDefaults({ allowEdit, leadDefaults, onChange }: Props) {
  const { workspace } = useWorkspace()

  const hasFollowUpGroups = Boolean(workspace?.followUpGroups?.length)
  const customProperties = workspace?.properties?.map(property => property.title) || []
  const availableProperties = [
    'followUp',
    ...(hasFollowUpGroups ? ['followUpGroup'] : []),
    'followUpUserId',
    'temp',
    ...customProperties,
  ]
  const { custom, ...primaryDefaults } = leadDefaults || {}

  function update(property: string, value: any) {
    const onLead = leadProperties.includes(property as any)
    onChange({
      ...leadDefaults,
      ...(onLead
        ? { [property]: value }
        : { custom: { ...leadDefaults?.custom, [property]: value } }),
    })
  }

  function remove(property: string) {
    if (!leadDefaults) return

    const onLead = leadProperties.includes(property as any)
    let updated: IEvent['leadDefaults'] = { ...leadDefaults }
    if (onLead) {
      const { [property as keyof Lead]: removed, ...rest } = leadDefaults
      updated = rest
      if (isEmpty(rest)) updated = undefined
    } else {
      const { [property]: removed, ...rest } = leadDefaults.custom || {}
      updated.custom = rest
      if (isEmpty(rest)) delete updated.custom
    }

    onChange(updated)
  }

  return (
    <Box>
      <Text textStyle="bodySmall" fontWeight="semibold" mb={3}>
        Lead Defaults
      </Text>

      <Flex flexDirection="column" gap={4} mb={4}>
        {Object.entries({ ...primaryDefaults, ...custom }).map(([key, value]) => {
          return (
            <Flex key={key} gap={4} align="flex-end">
              <ValueEditor
                allowEdit={allowEdit}
                property={key as keyof Lead}
                value={value}
                onChange={newValue => update(key, newValue)}
              />
              <IconButton
                aria-label="Remove default"
                icon={<TrashIcon size="sm" />}
                mb={2}
                size="sm"
                onClick={() => remove(key)}
              />
            </Flex>
          )
        })}
      </Flex>

      <Menu
        placeholder="Select a property to set default"
        value=""
        hideCheckbox
        options={availableProperties
          .filter(prop => {
            if (!leadDefaults) return true
            if (prop in leadDefaults || prop in (leadDefaults.custom ?? {})) return false
            if (prop === 'followUpGroup' && leadDefaults.followUpUserId !== undefined) return false
            if (prop === 'followUpUserId' && leadDefaults.followUpGroup !== undefined) return false
            return true
          })
          .map(prop => ({
            value: prop,
            label: formatLabel(prop),
          }))}
        onChange={value => update(value, null)}
        mb={3}
      />
    </Box>
  )
}

function formatLabel(property: string) {
  if (!leadProperties.includes(property as any)) return property
  if (property === 'temp') return 'Temperature'
  if (property === 'followUpUserId') return 'Follow Up User'
  return startCase(property)
}

interface ValueEditorProps {
  allowEdit: boolean
  property: keyof Lead
  value: any
  onChange: (value: any) => void
}

function ValueEditor({ allowEdit, property, value, onChange }: ValueEditorProps) {
  const { workspace } = useWorkspace()

  switch (property) {
    case 'followUp':
      return (
        <CheckBox checked={value} onChange={onChange} disabled={!allowEdit}>
          Follow Up
        </CheckBox>
      )
    case 'temp': {
      return (
        <Menu
          placeholder="Select temperature"
          label="Temperature"
          value={value as LeadTemperature}
          options={leadTemperatures.map(key => ({
            value: key,
            label: (
              <Flex align="center" gap={3}>
                <LeadTempIcon value={key} />
                <Text textStyle="bodySmall" fontWeight="500">
                  {startCase(key)}
                </Text>
              </Flex>
            ),
          }))}
          onChange={onChange}
          disabled={!allowEdit}
        />
      )
    }
    case 'followUpGroup':
      return (
        <Menu
          label="Follow Up Group"
          placeholder="Select group"
          value={value}
          options={workspace?.followUpGroups?.map(group => ({ value: group.title })) || []}
          onChange={onChange}
          disabled={!allowEdit}
        />
      )
    case 'followUpUserId':
      return (
        <UserSelect
          label="Follow Up User"
          minWidth="200px"
          value={value}
          onChange={onChange}
          permissionsAllowed={['admin', 'editor']}
          disabled={!allowEdit}
        />
      )
    default:
      return (
        <TextField
          label={property}
          value={value || ''}
          onChange={e => {
            const val = e.target.value
            const numVal = Number(val)
            onChange(typeof value === 'number' ? numVal : val)
          }}
          disabled={!allowEdit}
        />
      )
  }
}
